import { useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import type {
  CalendarEventType,
  CalendarComponentEvent,
  CalendarPermissions,
} from '@axiom/validation';

import { useOnDependencyUpdate } from '../../../hooks/useOnDependencyUpdate';

import { CalendarEventsUtil } from './calendar-events-util';
import { useCalendarMergeEvents } from './useCalendarMergeEvents';

type UseCalendarEventStateParams = {
  events: CalendarEventType[];
  backgroundEvents: CalendarEventType[];
  perms: CalendarPermissions;
  timezone?: string;
  bgEventIdsSet: Set<CalendarEventType['id']>;
  onChange?: (
    data: CalendarEventType[],
    bgData: CalendarEventType[]
  ) => void | Promise<void>;
  editableEventId?: string | null;
};

type UseCalendarEventStateReturn = {
  eventData: CalendarComponentEvent[];
  backgroundEventData: CalendarComponentEvent[];
  eventDataRef: React.MutableRefObject<CalendarComponentEvent[]>;
  backgroundEventDataRef: React.MutableRefObject<CalendarComponentEvent[]>;
  setEventData: React.Dispatch<React.SetStateAction<CalendarComponentEvent[]>>;
  setBackgroundEventData: React.Dispatch<
    React.SetStateAction<CalendarComponentEvent[]>
  >;
};

export const useCalendarEventState = ({
  events,
  backgroundEvents,
  perms,
  timezone,
  bgEventIdsSet,
  onChange,
  editableEventId,
}: UseCalendarEventStateParams): UseCalendarEventStateReturn => {
  const mergeEvents = useCalendarMergeEvents();

  const [eventData, setEventData] = useState(() => {
    const cleaned = CalendarEventsUtil.cleanForegroundEventsForState(
      events,
      perms,
      timezone,
      editableEventId
    );
    return mergeEvents(cleaned);
  });
  const [backgroundEventData, setBackgroundEventData] = useState(
    CalendarEventsUtil.cleanBackgroundEventsForState({
      events: backgroundEvents,
      permissions: perms,
      bgEventIdsSet,
      timezone,
    })
  );

  // Refs to track latest state for callbacks
  const eventDataRef = useRef(eventData);
  const backgroundEventDataRef = useRef(backgroundEventData);

  // Sync foreground events from props
  useOnDependencyUpdate(() => {
    const cleaned = CalendarEventsUtil.cleanForegroundEventsForState(
      events,
      perms,
      timezone,
      editableEventId
    );
    const merged = mergeEvents(cleaned);

    // "isEqual" to prevent render looping
    if (!isEqual(merged, eventData)) {
      setEventData(merged);
    }
  }, [events]);

  // Sync background events from props
  useOnDependencyUpdate(() => {
    const data = CalendarEventsUtil.cleanBackgroundEventsForState({
      events: backgroundEvents,
      permissions: perms,
      bgEventIdsSet,
      timezone,
    });

    // "isEqual" to prevent render looping
    if (!isEqual(data, backgroundEventData)) {
      setBackgroundEventData(data);
    }
  }, [backgroundEvents]);

  // Keep refs in sync with state
  useEffect(() => {
    eventDataRef.current = eventData;
  }, [eventData]);

  useEffect(() => {
    backgroundEventDataRef.current = backgroundEventData;
  }, [backgroundEventData]);

  // Call onChange when eventData changes
  useOnDependencyUpdate(() => {
    /**
     * This is not run on the 'on mount' render cycle
     * since it is only needed if "eventData" changes
     * aka a new or modified event from the user
     * */
    void onChange?.(
      [...CalendarEventsUtil.translateToStartTimeEndTimeProps(eventData)],
      [
        ...CalendarEventsUtil.translateToStartTimeEndTimeProps(
          backgroundEventData
        ),
      ]
    );
  }, [eventData]);

  // Handle timezone changes
  useOnDependencyUpdate(() => {
    /**
     * This is not run on the 'on mount' render cycle
     * since it is only needed if "timezone" changes.
     *
     * When timezone changes:
     * 1. Keep SAVED events as SAVED (timezone stored separately, no re-save needed)
     * 2. Re-validate all events for errors (timezone change may invalidate slots)
     * 3. Trigger onChange to notify parent form (allows submission when only timezone changed)
     *
     * Error handling is a fix for bug: FR-5706
     * Bug fix for FR-6829: SAVED events should not be marked as invalid when timezone changes
     * */
    setEventData(prevState => {
      // Clear errors but preserve event states
      const clearedEvents = prevState.map(event => ({
        ...event,
        error: null,
      }));

      // Re-validate all events for errors in new timezone.
      // Background events (e.g. freetime blocks while scheduling an interview)
      // must be included so busy foreground events can be validated against
      // them — otherwise valid interviews are wrongly marked Unavailable.
      const revalidated = CalendarEventsUtil.setEventError(
        {
          foreground: clearedEvents,
          background: backgroundEventDataRef.current,
        },
        perms
      );

      // Trigger onChange to notify parent that timezone affected the calendar
      // This allows forms to enable submission when timezone changes
      void onChange?.(
        [...CalendarEventsUtil.translateToStartTimeEndTimeProps(revalidated)],
        [
          ...CalendarEventsUtil.translateToStartTimeEndTimeProps(
            backgroundEventDataRef.current
          ),
        ]
      );

      return revalidated;
    });
  }, [timezone]);

  return {
    eventData,
    backgroundEventData,
    eventDataRef,
    backgroundEventDataRef,
    setEventData,
    setBackgroundEventData,
  };
};
